import React from 'react';

import {
	CourseOfferingEntity, PlanSelectionEntity, ProgramRuleEntity, RequirementEntity,
} from 'Models/Entities';

import { DataIndex } from 'Util/DataIndexUtils';
import { IGeneratedPlanFeedback } from 'Queries/Hooks/StudyPlan/types';
import {
	RequirementNode,
} from 'Views/Pages/Frontend/StudyPlanWizard/RequirementTreeGeneration';
import { CourseSelectionItem, CourseSelectionItemProps }
	from 'Views/Pages/Frontend/StudyPlanWizard/CourseSelection/RequirementContainer/CourseSelectionItem';
import { observer } from 'mobx-react';

const compareOfferingCoursesByCourseCode = (
	offeringIdsByCourseId: DataIndex<string[]>,
	courseOfferingsById: DataIndex<CourseOfferingEntity>,
) => {
	return (courseIdA: string, courseIdB: string) => {
		// retrieve course stored inside each course offering
		// (use the first course offering for each course id to retrieve the course entity)
		const courseAOfferingIds = offeringIdsByCourseId[courseIdA];
		const { course: courseA } = courseOfferingsById[courseAOfferingIds[0]];

		const courseBOfferingIds = offeringIdsByCourseId[courseIdB];
		const { course: courseB } = courseOfferingsById[courseBOfferingIds[0]];

		return courseA.code.localeCompare(courseB.code);
	};
};

interface ICourseListProps {
	requirement: RequirementEntity | ProgramRuleEntity,
	offeringIdsByCourseId: RequirementNode['offeringIdsByCourseId'],
	courseOfferingsById: DataIndex<CourseOfferingEntity>;
	planSelections: PlanSelectionEntity[];
	completedCourses: string[];
	duplicateCourses: string[];
	requisiteWarningsByCourseId: DataIndex<IGeneratedPlanFeedback['requisiteWarnings']>;
	updatePlanSelections: CourseSelectionItemProps['updatePlanSelections'];
	preferredCampusId: string;
}

const CourseSelectionList = observer(({
	requirement,
	offeringIdsByCourseId,
	courseOfferingsById,
	planSelections,
	completedCourses,
	duplicateCourses,
	requisiteWarningsByCourseId,
	updatePlanSelections,
	preferredCampusId,
}: ICourseListProps) => {
	const courseIds = Object.keys(offeringIdsByCourseId);

	if (courseIds.length === 0) {
		return (
			<div className="empty-state">
				<p>No courses</p>
			</div>
		);
	}

	const sortedCourseIds = courseIds
		.sort(compareOfferingCoursesByCourseCode(
			offeringIdsByCourseId,
			courseOfferingsById,
		));

	return (
		<>
			{
				sortedCourseIds
					.map(courseId => {
						const offeringIdsForCourse = offeringIdsByCourseId[courseId];
						const courseOfferings = offeringIdsForCourse
							.map(offeringId => courseOfferingsById[offeringId]);

						const firstOffering = courseOfferings[0];
						const { course } = firstOffering;

						const existingStudyPlanCourse = planSelections
							.find(
								studyPlanCourse => studyPlanCourse.courseId === courseId
								&& studyPlanCourse.locationId === requirement.id,
							);

						const selectedOfferingId = existingStudyPlanCourse?.courseOfferingId;

						const selectedCampusId = selectedOfferingId
							? courseOfferingsById[selectedOfferingId].campusId
							: undefined;

						const completed = !!completedCourses
							.find(completedCourse => completedCourse === courseId);

						const { compulsory } = requirement;

						const itemKey = `${course.id}-${firstOffering.id}`;

						const hasDuplicates = !!duplicateCourses
							.find(duplicateCourse => duplicateCourse === course.id);

						return (
							<CourseSelectionItem
								key={itemKey}
								requirement={requirement}
								course={course}
								selected={existingStudyPlanCourse !== undefined}
								completed={completed}
								hasDuplicates={hasDuplicates}
								compulsory={compulsory}
								courseOfferings={courseOfferings}
								selectedOfferingId={selectedOfferingId}
								selectedCampusId={selectedCampusId}
								requisiteWarningsByCourseId={requisiteWarningsByCourseId}
								updatePlanSelections={updatePlanSelections}
								preferredCampusId={preferredCampusId}
							/>
						);
					})
			}
		</>
	);
});

export default CourseSelectionList;
